<template>
  <div class="role-form">
    <div class="role-form-header">
      <drawer-dialog-controls
        :is-pristine="isPristine"
        :is-loading="isLoading"
        @close="$emit('close')"
        @save="saveWithTrim"
      >
        {{ dialogTitle }}
      </drawer-dialog-controls>
    </div>
    <div class="role-form-content">
      <v-card>
        <v-card-title>
          Role Settings
        </v-card-title>

        <v-divider />

        <v-container>
          <v-row class="align-end">
            <v-col cols="4">
              <select-project
                v-model="localModelValue.entity_id"
                class="mt-0 pt-0"
                :project-list-custom="projectList"
                required
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="localModelValue.name"
                class="mt-0 pt-0"
                label="Role Name"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="localModelValue.description"
                class="mt-0 pt-0"
                label="Role Description"
                rows="3"
                hide-details
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card class="mt-3">
        <v-card-title>
          Project settings
        </v-card-title>
        <v-divider />

        <v-container>
          <v-row class="mx-0">
            <v-col cols="12">
              <v-select
                v-model="localModelValue.entities"
                :items="Object.values(entitiesById)"
                :return-object="false"
                :readonly="readonly"
                label="Projects"
                multiple
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card class="mt-3">
        <v-card-title>
          Role Permissions
        </v-card-title>

        <v-divider />

        <v-container>
          <div class="permissions pa-0">
            <v-card
              class="mb-3 permission-card"
              v-for="(permissions, permissionGroup) in permissionsByGroup"
              :key="permissionGroup"
            >
              <v-card-title class="permission-group-title">
                {{ capitalize(permissionGroup) }}
                <v-checkbox
                  :input-value="getSelectAllValue(permissionGroup)"
                  :indeterminate="getIsIndeterminate(permissionGroup)"
                  class="ma-0"
                  hide-details
                  label="Select All"
                  :readonly="readonly"
                  @change="toggleGroup(permissionGroup, $event)"
                />
              </v-card-title>
              <v-divider />

              <v-row class="ma-0 pa-3">
                <v-checkbox
                  v-for="permission in permissions"
                  v-if="permission.type === 'boolean'"
                  v-model="localModelValue.permissions[permission.key]"
                  style="flex:50%;"
                  class="mt-1"
                  hide-details
                  :key="permission.key"
                  :readonly="readonly"
                  :label="permission.name"
                />
              </v-row>

              <v-row class="mx-0 px-4">
                <v-select
                  v-for="permission in permissions"
                  v-if="permission.type === 'multiple' && permission.list.length"
                  :key="permission.key"
                  v-model="localModelValue.permissions[permission.key]"
                  multiple
                  small-chips
                  :label="permission.name"
                  auto-select-first
                  :items="permission.list"
                  class="d-inline-flex"
                  style="min-width: 320px;"
                />
              </v-row>
            </v-card>
          </div>
        </v-container>
      </v-card>
    </div>
  </div>
</template>

<script>
  import { cloneDeep, capitalize } from 'lodash'
  import { DrawerDialogControls } from '../../../components/core/DrawerDialog'
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'

  const FIELDS_TO_TRIM = [ 'name', 'description' ]

  export default {
    props: {
      role: {
        type: Object,
        default: () => ({}),
      },
      projectList: {
        type: Array,
        default: () => ([]),
      },
      permissionsByGroup: {
        type: Object,
        default: () => ({}),
      },
      entitiesById: {
        type: Object,
        default: () => ({}),
      },
      isLoading: Boolean,
      readonly: Boolean
    },
    components: {
      SelectProject,
      DrawerDialogControls
    },
    data () {
      return {
        localModelValue: {},
        isPristine: true,
      }
    },
    created () {
      this.localModelValue = cloneDeep(this.role)

      for (const group in this.permissionsByGroup) {
        for (const permission of this.permissionsByGroup[group]) {
          const { key, type } = permission
          if (!(key in this.localModelValue.permissions)) {
            this.$set(this.localModelValue.permissions, key, type === 'boolean' ? false : [])
          }
        }
      }

      const unwatch = this.$watch('localModelValue', () => {
        this.isPristine = false
        unwatch()
      }, { deep: true })
    },
    computed: {
      dialogTitle () {
        return this.role.role_id ? `Edit role ${this.role.role_id}: ${this.role.name}` : 'Add new role'
      },
    },
    methods: {
      capitalize,
      getSelectAllValue (group) {
        return this.permissionsByGroup[group].every(permission => {
          return permission.type === 'multiple' || this.localModelValue.permissions[permission.key]
        })
      },
      getIsIndeterminate (group) {
        const boolPermissions = this.permissionsByGroup[group].filter(({type}) => type === 'boolean')
        const enabledBoolPermissions = boolPermissions.filter(({key}) => this.localModelValue.permissions[key])

        return !!enabledBoolPermissions.length && enabledBoolPermissions.length !== boolPermissions.length
      },
      toggleGroup (group, value) {
        for (const permission of this.permissionsByGroup[group]) {
          if (permission.type === 'boolean') {
            this.localModelValue.permissions[permission.key] = !!value
          }
        }
      },
      saveWithTrim () {
        for (const field of FIELDS_TO_TRIM) {
          if (this.localModelValue[field]) {
            this.localModelValue[field] = this.localModelValue[field].trim()
          }
        }

        this.$emit('save', this.localModelValue)
      }
    }
  }
</script>

<style lang="scss">
  .role-form {
    border-radius: 0 !important;

    &-header {
      position: sticky;
      top: 0;
      left: 0;
      z-index: 1;
    }

    &-content {
      background-color: #F2F2F2;
      padding: 1rem;

      .permissions {
        columns: 2;

        //padding: 1rem;
        //gap: 1rem;

        .permission-card {
          break-inside: avoid-column;
          //width: calc(50% - 1rem);
          .permission-group-title {
            justify-content: space-between;
          }
        }
      }
    }
  }
</style>
